/**
 * Thin wrapper around fetch + MSAL token acquisition.
 * Redirects to login if silent token acquisition fails.
 */

import { msalInstance, loginRequest } from '@/config/msalConfig';

const API_BASE =
  import.meta.env.VITE_API_BASE ||
  (import.meta.env.DEV ? 'http://localhost:8000' : '');

const AUTH_RETRY_KEY = 'nhanalytics_auth_retry';

/**
 * Fetch from the backend API with automatic Bearer token.
 * Redirects to Azure AD login if token acquisition fails.
 */
export async function apiFetch(
  path: string,
  options: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(options.headers);

  // Acquire Azure AD token
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    try {
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      headers.set('Authorization', `Bearer ${tokenResponse.idToken}`);
    } catch {
      // Silent acquisition failed — redirect to login
      msalInstance.acquireTokenRedirect(loginRequest);
      // Return a never-resolving promise so callers don't proceed
      return new Promise(() => {});
    }
  }

  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (response.status === 401) {
    // Check if we already retried — prevent infinite loop
    const alreadyRetried = sessionStorage.getItem(AUTH_RETRY_KEY);
    if (alreadyRetried) {
      // Already retried once — clear flag, log out, show login page
      sessionStorage.removeItem(AUTH_RETRY_KEY);
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin + '/',
      });
      return new Promise(() => {});
    }

    // First 401 — set retry flag and force fresh authentication
    sessionStorage.setItem(AUTH_RETRY_KEY, '1');
    const accts = msalInstance.getAllAccounts();
    if (accts.length > 0) {
      msalInstance.acquireTokenRedirect({
        ...loginRequest,
        account: accts[0],
      });
    } else {
      msalInstance.loginRedirect(loginRequest);
    }
    return new Promise(() => {});
  }

  // Success — clear any retry flag
  sessionStorage.removeItem(AUTH_RETRY_KEY);
  return response;
}
